import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Elements as StripeElements } from '@stripe/react-stripe-js';
import {
  RecurlyProvider,
  Elements as RecurlyElements,
} from '@recurly/react-recurly';

import { PaymentSystem } from '../../constants';
import { getPaymentSystem, getPublicKeys } from '../../utils/storage';

const stripeInstances = {};

const Provider = ({ children }) => {
  const paymentSystem = getPaymentSystem();
  const publicKeys = getPublicKeys();
  const stripePublicKey = publicKeys?.stripe?.public_key;
  const [stripe, setStripe] = useState(null);
  const [isCollectJsLoaded, setIsCollectJsLoaded] = useState(() => !!window.CollectJS);

  // NOTE: this deliberately does NOT depend on `paymentSystem`. That value comes
  // from module state written by `ReactBilling.init`, not from React — so when this
  // provider first renders before init has run, the system is not TAILOREDPAY yet
  // and there is nothing to re-trigger the effect once it becomes so. Bailing out
  // on it left the poll unstarted forever, and with it `isCollectJsLoaded` false —
  // meaning this provider returned null for good and the payment modal (its child)
  // was never rendered at all. The system is re-read on every tick instead, and the
  // interval stops on its own once another provider turns out to own the page.
  useEffect(() => {
    if (isCollectJsLoaded) {
      return undefined;
    }

    let isMounted = true;

    const intervalId = window.setInterval(() => {
      const currentPaymentSystem = getPaymentSystem();

      if (currentPaymentSystem && currentPaymentSystem !== PaymentSystem.TAILOREDPAY) {
        window.clearInterval(intervalId);
        return;
      }

      if (window.CollectJS && isMounted) {
        window.clearInterval(intervalId);
        setIsCollectJsLoaded(true);
      }
    }, 50);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [isCollectJsLoaded]);

  useEffect(() => {
    if (paymentSystem !== PaymentSystem.STRIPE) {
      setStripe(null);
      return undefined;
    }

    if (!stripePublicKey) {
      return undefined;
    }

    let isMounted = true;

    const initStripe = () => {
      if (window.Stripe && isMounted) {
        stripeInstances[stripePublicKey] =
          stripeInstances[stripePublicKey] || window.Stripe(stripePublicKey);
        setStripe((currentStripe) => (
          currentStripe === stripeInstances[stripePublicKey]
            ? currentStripe
            : stripeInstances[stripePublicKey]
        ));
        return true;
      }

      return false;
    };

    if (initStripe()) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      if (initStripe()) {
        window.clearInterval(intervalId);
      }
    }, 50);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [paymentSystem, stripePublicKey]);

  if (paymentSystem === PaymentSystem.RECURLY) {
    return (
      <RecurlyProvider publicKey={publicKeys.recurly?.public_key}>
        <RecurlyElements>{children}</RecurlyElements>
      </RecurlyProvider>
    );
  }
  if (paymentSystem === PaymentSystem.STRIPE) {
    if (!stripe) {
      return null;
    }

    return <StripeElements stripe={stripe}>{children}</StripeElements>;
  }
  if (paymentSystem === PaymentSystem.SOLIDGATE) {
    return <React.Fragment>{children}</React.Fragment>;
  }
  if (paymentSystem === PaymentSystem.PADDLE) {
    return <React.Fragment>{children}</React.Fragment>;
  }
  // Collect.js has no React context — it binds to DOM containers rendered by the
  // TailoredPay CardElement itself. But the script loads asynchronously, so hold
  // the children back until `window.CollectJS` exists: mounting the card fields
  // first means Collect.js has nothing to attach its iframes to.
  if (paymentSystem === PaymentSystem.TAILOREDPAY) {
    if (!isCollectJsLoaded) {
      return null;
    }

    return <React.Fragment>{children}</React.Fragment>;
  }
  return null;
};

Provider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default Provider;
